import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, findIndex } from 'lodash';
import moment from 'moment-timezone';
import {
  Itinerary,
  Location,
  Organization,
  OrganizationRider,
  OrganizationRiderListItem,
  OrganizationRiderServiceLevel,
  OrganizationServiceLevel,
  PaymentCollectionType,
  RideRequest,
  RideRequestInfo,
  RideRequestLocation,
  ServiceLevelConstants,
  ServiceLevelType,
  TripListItem
} from '@uzurv/rideWss/clara';
import { RideStatusConstants } from '../constants';
import { ApplicationException } from '../exceptions';
import { RideGroup } from '../store/rides/rides.reducer';
import { isNullOrUndefined } from './is-null-or-defined';

export class RideUtil {
  static isInfiniteScrollEnabled(rideListItems: Array<TripListItem>, pageSize: number): boolean {
    return rideListItems.length === pageSize && rideListItems.length !== 0;
  }

  static isRideInExecution(ride: TripListItem): boolean {
    return (
      ride?.status === RideStatusConstants.ON_DEMAND ||
      ride?.status === RideStatusConstants.DRIVER_EN_ROUTE ||
      ride?.status === RideStatusConstants.IN_PROGRESS ||
      ride?.status === RideStatusConstants.PICKUP_ARRIVED ||
      ride?.status === RideStatusConstants.DROP_OFF_ARRIVED
    );
  }

  static removeRideFromList(ride: TripListItem, ridesList: Array<TripListItem>): Array<TripListItem> {
    const clonedRide: TripListItem = cloneDeep(ride);
    const clonedRideList: Array<TripListItem> = cloneDeep(ridesList);
    const index = findIndex(clonedRideList, { rideRequestId: ride.rideRequestId });
    if (index > -1) {
      clonedRideList.splice(index, 1);
    }
    return clonedRideList;
  }

  static addOrReplaceRideInList(ride: TripListItem, ridesList: Array<TripListItem>, ascending: boolean): Array<TripListItem> {
    if (isNullOrUndefined(ridesList)) {
      return [ride];
    }

    const clonedRide: TripListItem = cloneDeep(ride);
    const clonedRideList: Array<TripListItem> = cloneDeep(ridesList);
    const index = findIndex(clonedRideList, { rideRequestId: ride.rideRequestId });
    if (index > -1) {
      clonedRideList.splice(index, 1, clonedRide);
    } else {
      clonedRideList.push(clonedRide);
    }
    return RideUtil.sortRideListItemsByPickupDate(clonedRideList, ascending);
  }

  static groupRides(rides: Array<TripListItem>, dateFormat: string): Array<RideGroup> {
    const rideGroups: Array<RideGroup> = new Array<RideGroup>();
    rides.forEach(ride => {
      const groupKey: string = moment(ride.pickupDate).tz(ride.timeZone).format(dateFormat);

      const rideGroup: RideGroup = rideGroups.find(rideGroup => {
        return rideGroup.timePeriod === groupKey;
      });

      if (isNullOrUndefined(rideGroup)) {
        const rideGroup: RideGroup = { timePeriod: groupKey, rideListItems: new Array<TripListItem>() };
        rideGroup.rideListItems.push(ride);
        rideGroups.push(rideGroup);
      } else {
        rideGroup.rideListItems.push(ride);
      }
    });

    return rideGroups;
  }

  static sortRideListItemsByPickupDate(rideListItems: Array<TripListItem>, ascending = true): Array<TripListItem> {
    if (isNullOrUndefined(rideListItems)) {
      return [];
    }

    return rideListItems.sort((a, b) => {
      let aSortDate = a.activatedAt;
      let bSortDate = b.activatedAt;

      // Mimic backend sorting

      if (isNullOrUndefined(aSortDate)) {
        if (!isNullOrUndefined(a.scheduledPickupTime)) {
          aSortDate = a.scheduledPickupTime;
        }
      }

      if (isNullOrUndefined(bSortDate)) {
        if (!isNullOrUndefined(b.scheduledPickupTime)) {
          bSortDate = b.scheduledPickupTime;
        }
      }

      // Group within pickup date

      if (isNullOrUndefined(aSortDate) || isNullOrUndefined(bSortDate)) {
        if (!moment(a.pickupDate).isSame(b.pickupDate, 'day')) {
          aSortDate = a.pickupDate;
          bSortDate = b.pickupDate;
        }
      }

      if (isNullOrUndefined(aSortDate)) {
        return ascending ? 1 : -1;
      }

      if (isNullOrUndefined(bSortDate)) {
        return ascending ? -1 : 1;
      }

      if (ascending) {
        return aSortDate.getTime() - bSortDate.getTime();
      } else {
        return bSortDate.getTime() - aSortDate.getTime();
      }
    });
  }

  /**
   * Sorts an array of ride requests by pickupDate
   *
   */
  static sortRideRequestsByPickupDate(rideRequests: Array<RideRequest>, ascending = true): Array<RideRequest> {
    if (isNullOrUndefined(rideRequests)) {
      return [];
    }

    return rideRequests.sort((a, b) => {
      const aSortDate = a.pickupDate;
      const bSortDate = b.pickupDate;

      // If pickup date of first ride request is null, return a value based on the ascending flag.
      // If ascending is true, return 1 (put "a" after "b"), otherwise -1 (put "a" before "b").
      if (isNullOrUndefined(aSortDate)) {
        return ascending ? 1 : -1;
      }

      // If the pickup date of the second ride request is null, return a value based on the ascending flag.
      // If ascending is true, return -1 (put "b" after "a"), otherwise 1 (put "b" before "a").
      if (isNullOrUndefined(bSortDate)) {
        return ascending ? -1 : 1;
      }

      // If both dates are valid, compare them based on the ascending flag.
      // If ascending true, return dates in ascending order (earlier dates come first).
      // If ascending false, return dates in descending order (later dates come first).
      if (ascending) {
        return aSortDate.getTime() - bSortDate.getTime();
      } else {
        return bSortDate.getTime() - aSortDate.getTime();
      }
    });
  }

  /**
   * Takes a Location and returns a RideRequestLocation
   *
   * @param location
   */
  static locationToRideRequestLocation(location: Location): RideRequestLocation {
    if (isNullOrUndefined(location)) {
      return null;
    }

    const rideRequestLocation = new RideRequestLocation();
    rideRequestLocation.locationId = location.id;
    rideRequestLocation.display = location.display;
    rideRequestLocation.latitude = location.latitude;
    rideRequestLocation.longitude = location.longitude;
    return rideRequestLocation;
  }

  /**
   * Populates itinerary and ride request properties from rideRequestInfo and organizationRiderListItems.
   * @param itinerary
   * @param currentRideRequestLeg
   * @param rideRequestInfo
   * @param organizationRiderListItem
   * @param riderId
   */
  static processItineraryForSubmission(
    itinerary: Itinerary,
    currentRideRequestLeg: RideRequest,
    rideRequestInfo: RideRequestInfo,
    organizationRiderListItem: OrganizationRiderListItem,
    riderId: number
  ): Itinerary {
    currentRideRequestLeg.dropoffDateProvided = false;

    if (currentRideRequestLeg.rideRequestType.onDemand) {
      currentRideRequestLeg.pickupDate = null;
    }

    currentRideRequestLeg.distanceEstimate = rideRequestInfo.distanceEstimate;
    currentRideRequestLeg.durationEstimate = rideRequestInfo.durationEstimate;

    if (!isNullOrUndefined(rideRequestInfo.tolls) && isNullOrUndefined(currentRideRequestLeg.tollEstimate)) {
      currentRideRequestLeg.tollEstimate = rideRequestInfo.tolls;
    }

    if (!isNullOrUndefined(rideRequestInfo.riderFee)) {
      currentRideRequestLeg.requestFee = rideRequestInfo.riderFee;
    }

    if (!isNullOrUndefined(rideRequestInfo.polylineId)) {
      currentRideRequestLeg.polylineId = rideRequestInfo.polylineId;
    }

    currentRideRequestLeg.dropoffDate = rideRequestInfo.estimatedDropoffDate;

    // No Preference has a value of -1 for front end validation
    // Map that to null
    if (currentRideRequestLeg.preferredProviderId === -1) {
      currentRideRequestLeg.preferredProviderId = null;
    }

    const riderPaymentRequired: boolean = organizationRiderListItem.riderPaymentRequired;

    // Make sure billable passenger count is 0 if no payment required
    if (!riderPaymentRequired) {
      currentRideRequestLeg.billablePassengerCount = 0;
    }

    // Create the organization for the Itinerary
    const organization: Organization = new Organization();
    organization.id = organizationRiderListItem.organizationId;

    // Create the organizationRider for the itinerary
    const organizationRider: OrganizationRider = new OrganizationRider();
    organizationRider.id = organizationRiderListItem.organizationRiderId;
    organizationRider.rider.id = riderId;

    // Set the new itinerary properties based on the objects created.
    itinerary.organization = organization;
    itinerary.organizationRider = organizationRider;
    itinerary.organizationRider.rider.id = riderId;

    itinerary.rideRequests.push(currentRideRequestLeg);
    itinerary.rideRequests = RideUtil.sortRideRequestsByPickupDate(itinerary.rideRequests);

    return itinerary;
  }

  /**
   * A itinerary ready for ride creation.
   */
  static initializeNewItinerary(): Itinerary {
    const itinerary: Itinerary = new Itinerary();
    itinerary.rideRequests = [];
    return itinerary;
  }

  static initializeNewRideRequestLeg(selectedOrganizationRiderListItem: OrganizationRiderListItem): RideRequest {
    const currentRideRequestLeg: RideRequest = new RideRequest();
    currentRideRequestLeg.passengerCount = null;

    if (selectedOrganizationRiderListItem.riderPaymentRequired) {
      currentRideRequestLeg.billablePassengerCount = currentRideRequestLeg.passengerCount;
    } else {
      currentRideRequestLeg.billablePassengerCount = 0;
    }
    return currentRideRequestLeg;
  }

  /**
   * Formats selectedDaysOfTheWeek with spacing and pluralization.
   */
  static formatSelectedDaysOfTheWeek(selectedDaysOfTheWeek: Array<string>, translateService: TranslateService): string {
    if (isNullOrUndefined(selectedDaysOfTheWeek)) {
      return;
    }

    const pluralDays: Array<string> = selectedDaysOfTheWeek.map(day => day + 's');
    const daysString: string = pluralDays.join(', ');
    // Replace the last occurrence of "," with "and"
    const regex = /,(?=[^,]*$)/;
    if (pluralDays.length === 2) {
      return daysString.replace(regex, ' ' + translateService.instant('and') + ' ');
    } else {
      return daysString.replace(regex, ', ' + translateService.instant('and') + ' ');
    }
  }

  /**
   * Used to determine if ride is eligible for ride backup
   *
   * @param rideRequest
   * @param rideRequestInfo
   * @param riderPaymentRequired
   * @param riderPaymentAccountEnabled
   * @param organizationServiceLevels
   * @param organizationRiderServiceLevels
   */
  static isRideRequestRideBackupEligible(
    rideRequest: RideRequest,
    rideRequestInfo: RideRequestInfo,
    riderPaymentRequired: boolean,
    riderPaymentAccountEnabled: boolean,
    organizationServiceLevels: Array<OrganizationServiceLevel>,
    organizationRiderServiceLevels: Array<OrganizationRiderServiceLevel>
  ): boolean {
    if (isNullOrUndefined(rideRequest)) {
      return false;
    }

    if (isNullOrUndefined(rideRequestInfo)) {
      return false;
    }

    if (riderPaymentRequired && !riderPaymentAccountEnabled) {
      return false;
    }

    const ac2cOrganizationServiceLevel: OrganizationServiceLevel = organizationServiceLevels?.find(
      osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE
    );

    // If the organization does not consider curb to curb as an optional service level,
    // then ride back up will default to true and they cannot change it
    if (!isNullOrUndefined(ac2cOrganizationServiceLevel) && !ac2cOrganizationServiceLevel.optional) {
      return false;
    }

    const ac2cRiderServiceLevel: OrganizationRiderServiceLevel = organizationRiderServiceLevels?.find(
      orsl => orsl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE
    );

    // If the rider is always ok with curb to curb,
    // then ride back up will default to true and they cannot change it
    if (ac2cRiderServiceLevel?.enabled) {
      return false;
    }

    return rideRequestInfo?.rideBackupEligible;
  }

  /**
   *
   * @param rideRequest the leg you want to update
   * @param serviceLevelType the selected service level type
   * @param organizationServiceLevels the enabled organization service levels for the rider
   * @returns your provided ride request with the correct minimum service level applied
   *
   * The minimum service level is determined based on the service level type, the organization service levels, and whether or not the ride backup is accepted
   * 1) if the requested service level type is ambulatory,
   *  a)  and ride backup is accepted,
   *    i. if curb to curb is an option, select it
   *    ii. else, if door to door is an option, select it
   *    iii. else throw an error
   *  b) if ride backup is not accepted
   *    i. if door to door is an option, select it
   *    ii. else if curb to curb is configured select it
   *    iii. else, throw an error
   * 2) if service level type is non-ambulatory, set service level to non-ambulatory door to door
   *  a) if missing, throw an error
   */
  static updateRideRequestServiceLevel(
    rideRequest: RideRequest,
    serviceLevelType: ServiceLevelType,
    organizationServiceLevels: Array<OrganizationServiceLevel>
  ): RideRequest {
    if (serviceLevelType === ServiceLevelType.AMBULATORY) {
      const ad2dServiceLevel = organizationServiceLevels.find(
        osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_DOOR_TO_DOOR_CODE
      );

      const ac2cServiceLevel = organizationServiceLevels.find(
        osl => osl.serviceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE
      );

      if (rideRequest.rideBackupAccepted) {
        if (!isNullOrUndefined(ac2cServiceLevel)) {
          rideRequest.serviceLevel = ac2cServiceLevel.serviceLevel;
        } else if (!isNullOrUndefined(ad2dServiceLevel)) {
          rideRequest.serviceLevel = ad2dServiceLevel.serviceLevel;
        } else {
          throw new ApplicationException('Missing required service level');
        }
      } else {
        if (!isNullOrUndefined(ad2dServiceLevel)) {
          rideRequest.serviceLevel = ad2dServiceLevel.serviceLevel;
        } else if (!isNullOrUndefined(ac2cServiceLevel)) {
          rideRequest.serviceLevel = ac2cServiceLevel.serviceLevel;
        } else {
          throw new ApplicationException('Missing required service level');
        }
      }
    } else {
      const nad2dServiceLevel = organizationServiceLevels.find(
        osl => osl.serviceLevel.code === ServiceLevelConstants.NON_AMBULATORY_DOOR_TO_DOOR_CODE
      );

      if (isNullOrUndefined(nad2dServiceLevel)) {
        throw new ApplicationException('Missing required service level');
      }

      rideRequest.serviceLevel = nad2dServiceLevel.serviceLevel;
    }

    return rideRequest;
  }

  static extractPaymentCollectionType(
    paymentCollectionTypeCode: string,
    organizationRiderListItems: Array<OrganizationRiderListItem>
  ): PaymentCollectionType | null {
    let paymentCollectionType: PaymentCollectionType | null = null;
    organizationRiderListItems.forEach(organizationRiderListItem => {
      organizationRiderListItem.supportedPaymentMethods.forEach(opm => {
        if (opm.paymentMethod.paymentCollectionType.code === paymentCollectionTypeCode) {
          paymentCollectionType = opm.paymentMethod.paymentCollectionType;
        }
      });
    });

    return paymentCollectionType;
  }
}
